import { Constants } from './constants';
import { isUkcRallyShow, ukcRallyClassLabel } from './ukc-rally-classes';

/**
 * Shared multi-entry mappings for both frontend validation and backend transformation
 * This ensures consistency between UI conflict detection and server-side processing
 */

export interface IMultiEntryMapping {
  multiEntryLevel: string;
  sanctioningClub: string;
  showType: string;
  constituentClasses: string[];
  displayName: string;
  /**
   * When true, selecting this multi-entry disables ALL other options in the
   * same show element — not just its constituent classes. Use for combo entries
   * like URX / RMX where the dog is ineligible for every other individual class.
   */
  disablesAll?: boolean;
}

export const MULTI_ENTRY_MAPPINGS: IMultiEntryMapping[] = [
  // AKC Obedience
  {
    multiEntryLevel: Constants.dog_class_element_level.udx,
    sanctioningClub: Constants.sanctioning_club.AKC,
    showType: Constants.show_type.obedience,
    constituentClasses: [
      Constants.dog_class_element_level.open_b,
      Constants.dog_class_element_level.utility_b
    ],
    displayName: 'UDX'
  },
  
  // AKC Rally Obedience
  {
    multiEntryLevel: Constants.dog_class_element_level.rae,
    sanctioningClub: Constants.sanctioning_club.AKC,
    showType: Constants.show_type.rally_obedience,
    constituentClasses: [
      Constants.dog_class_element_level.advanced_b,
      Constants.dog_class_element_level.excellent_b
    ],
    displayName: 'RAE'
  },
  {
    multiEntryLevel: Constants.dog_class_element_level.rach,
    sanctioningClub: Constants.sanctioning_club.AKC,
    showType: Constants.show_type.rally_obedience,
    constituentClasses: [
      Constants.dog_class_element_level.advanced_b,
      Constants.dog_class_element_level.excellent_b,
      Constants.dog_class_element_level.master
    ],
    displayName: 'RACH'
  },
  
  // UKC Obedience
  {
    multiEntryLevel: Constants.dog_class_element_level.uudx,
    sanctioningClub: Constants.sanctioning_club.UKC,
    showType: Constants.show_type.obedience,
    constituentClasses: [
      Constants.dog_class_element_level.open_b,
      Constants.dog_class_element_level.utility_b
    ],
    displayName: 'UUDX'
  },
  
  // UKC Rally Obedience
  {
    multiEntryLevel: Constants.dog_class_element_level.urx,
    sanctioningClub: Constants.sanctioning_club.UKC,
    showType: Constants.show_type.rally_obedience,
    constituentClasses: [
      Constants.dog_class_element_level.ro2_b,
      Constants.dog_class_element_level.ro3_b
    ],
    displayName: 'URX',
    disablesAll: true
  },
  {
    multiEntryLevel: Constants.dog_class_element_level.rmx,
    sanctioningClub: Constants.sanctioning_club.UKC,
    showType: Constants.show_type.rally_obedience,
    constituentClasses: [
      Constants.dog_class_element_level.ro2_b,
      Constants.dog_class_element_level.ro3_b,
      Constants.dog_class_element_level.rom_b
    ],
    displayName: 'RMX',
    disablesAll: true
  },
  
  // UKC Agility — GRACH runs in the B levels, GRACH A in the A levels,
  // ACH in Beginner B and Intermediate B.
  {
    multiEntryLevel: Constants.dog_class_element_level.ach,
    sanctioningClub: Constants.sanctioning_club.UKC,
    showType: Constants.show_type.agility,
    constituentClasses: [
      Constants.dog_class_element_level.beginner_b,
      Constants.dog_class_element_level.intermediate_b
    ],
    displayName: 'ACH'
  },
  {
    multiEntryLevel: Constants.dog_class_element_level.grach,
    sanctioningClub: Constants.sanctioning_club.UKC,
    showType: Constants.show_type.agility,
    constituentClasses: [
      Constants.dog_class_element_level.beginner_b,
      Constants.dog_class_element_level.intermediate_b,
      Constants.dog_class_element_level.excellent
    ],
    displayName: 'GRACH'
  },
  {
    multiEntryLevel: Constants.dog_class_element_level.grach_a,
    sanctioningClub: Constants.sanctioning_club.UKC,
    showType: Constants.show_type.agility,
    constituentClasses: [
      Constants.dog_class_element_level.beginner_a,
      Constants.dog_class_element_level.intermediate_a,
      Constants.dog_class_element_level.excellent
    ],
    displayName: 'GRACH A'
  },
  // Legacy GRACH B — no longer selectable for new shows; kept so existing
  // registrations continue to validate and transform.
  {
    multiEntryLevel: Constants.dog_class_element_level.grach_b,
    sanctioningClub: Constants.sanctioning_club.UKC,
    showType: Constants.show_type.agility,
    constituentClasses: [
      Constants.dog_class_element_level.beginner_b,
      Constants.dog_class_element_level.intermediate_b,
      Constants.dog_class_element_level.excellent
    ],
    displayName: 'GRACH B'
  }
];

/**
 * Utility functions for working with multi-entry mappings
 */
export class MultiEntryMappingsUtil {
  /**
   * Constituent classes are listed as "RO2 B" style labels. UKC Rally classes may be
   * stored as level RO2 + section B, so compare on the label.
   */
  static classLabel(level: string, section: string | null | undefined, sanctioningClub: string, showType: string): string {
    return isUkcRallyShow(sanctioningClub, showType) ? ukcRallyClassLabel(level, section) : level;
  }

  /** True when `level`/`section` is one of the multi-entry's constituent classes. */
  static isConstituentOf(mapping: IMultiEntryMapping, level: string, section?: string | null): boolean {
    return mapping.constituentClasses.includes(this.classLabel(level, section, mapping.sanctioningClub, mapping.showType));
  }

  /**
   * Get mapping for a specific multi-entry
   */
  static getMapping(level: string, sanctioningClub: string, showType: string): IMultiEntryMapping | undefined {
    return MULTI_ENTRY_MAPPINGS.find(mapping => 
      mapping.multiEntryLevel === level &&
      mapping.sanctioningClub === sanctioningClub &&
      mapping.showType === showType
    );
  }

  /**
   * Check if a level is a multi-entry
   */
  static isMultiEntry(level: string, sanctioningClub: string, showType: string): boolean {
    return !!this.getMapping(level, sanctioningClub, showType);
  }

  /**
   * Get constituent classes for a multi-entry
   */
  static getConstituentClasses(level: string, sanctioningClub: string, showType: string): string[] {
    const mapping = this.getMapping(level, sanctioningClub, showType);
    return mapping ? mapping.constituentClasses : [];
  }

  /**
   * Get multi-entries that contain a specific individual class
   */
  static getRelatedMultiEntries(level: string, sanctioningClub: string, showType: string, section?: string | null): IMultiEntryMapping[] {
    return MULTI_ENTRY_MAPPINGS.filter(mapping => 
      mapping.sanctioningClub === sanctioningClub &&
      mapping.showType === showType &&
      this.isConstituentOf(mapping, level, section)
    );
  }

  /**
   * Get display name for a multi-entry
   */
  static getDisplayName(level: string, sanctioningClub: string, showType: string): string | undefined {
    const mapping = this.getMapping(level, sanctioningClub, showType);
    return mapping?.displayName;
  }

  /**
   * Returns true if this multi-entry level should disable ALL other options in
   * the same show element when selected (not just its constituent classes).
   */
  static isDisablesAll(level: string, sanctioningClub: string, showType: string): boolean {
    const mapping = this.getMapping(level, sanctioningClub, showType);
    return !!mapping?.disablesAll;
  }
}
